// Recent activity (WEB_PLAN.md §5.3; iOS ActivityFeedView "Live feed"): completed activities by the visible people,
// newest first, the latest 50. Each row is ActivityRow with `showRecord`: the tinted type badge, "On-Site Visit ·
// Contacted", the store, "From New to Proposal" for stage changes, a note preview, "15 min", "By Jane Doe" and the
// time. Rows on a store the viewer can open link to it.
//
// The visit's photos and documents show under the entry with the store History look (ActivityEntry): 64 px
// thumbnails signed in one batch, "File not available" tiles, and "2 attachments (1 not available)" while some have
// not synced yet. They come from the viewer's own replica (`useAttachments`), so a rep sees only the files sync gave
// them. A thumbnail opens the photo viewer.

import { Clock, Paperclip, Radio } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { AttachmentPayload } from '@/contracts/models';
import { useAttachments, useUsers, type DataItem } from '@/data/hooks';
import { byId, fullName } from '@/domain/util';
import { DocumentRow, PhotoTile, PhotoViewer } from '@/features/stores/FileTiles';
import { Avatar, EmptyState, SectionCard, cn } from '@/ui';
import { ActivityGlyph } from './ActivityGlyph';
import {
  activityFiles,
  feedHeader,
  feedTime,
  missingFilesText,
  photoDetails,
  type ActivityFiles,
  type FeedModel,
  type FeedRow,
} from './dashboardModel';

export interface ActivityFeedCardProps {
  feed: FeedModel;
  now: Date;
  /** Reps see only their own entries ("Activities you log…"). */
  self: boolean;
  className?: string;
}

/** The photo open in the viewer, with the title of the entry it was logged with. */
interface OpenPhoto {
  item: DataItem<AttachmentPayload>;
  loggedWith: string;
}

export function ActivityFeedCard({ feed, now, self, className }: ActivityFeedCardProps) {
  const attachments = useAttachments();
  const users = useUsers();
  const usersById = useMemo(() => byId(users.values), [users]);
  const [photo, setPhoto] = useState<OpenPhoto | null>(null);

  return (
    <SectionCard
      title="Recent activity"
      description={feed.total > 0 ? feedHeader(feed) : undefined}
      padding="none"
      bodyClassName="overflow-hidden"
      className={className}
    >
      {feed.rows.length === 0 ? (
        <EmptyState
          compact
          icon={<Radio />}
          color="#009E9E"
          title="Nothing logged yet"
          message={
            self
              ? 'Visits, calls and notes you log in the Ground Team app show up here.'
              : 'Activities the team logs show up here as they sync.'
          }
        />
      ) : (
        <ol aria-label="Recent activity, newest first" className="divide-y divide-line">
          {feed.rows.map((row) => {
            const files = activityFiles(row.attachmentIds, (id) => attachments.get(id));
            return (
              <li key={row.id}>
                <FeedEntry row={row} now={now} />
                {files && (
                  // Outside the row's link: a thumbnail is a button of its own.
                  <ActivityFileStrip
                    files={files}
                    onOpenPhoto={(item) => setPhoto({ item, loggedWith: row.title })}
                    className="-mt-1.5 pr-4 pb-3 pl-[62px]"
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}
      {photo && (
        <PhotoViewer
          item={photo.item}
          onClose={() => setPhoto(null)}
          details={photoDetails(photo.item.value, { users: usersById, loggedWith: photo.loggedWith, now })}
        />
      )}
    </SectionCard>
  );
}

/**
 * An activity's photos (64 px thumbnails), documents (Open links) and the "N attachments (1 not available)" line,
 * as in a store's History entry. Shared with the rep day timeline.
 */
export function ActivityFileStrip({
  files,
  onOpenPhoto,
  className,
}: {
  files: ActivityFiles;
  onOpenPhoto: (item: DataItem<AttachmentPayload>) => void;
  className?: string;
}) {
  const missing = missingFilesText(files);
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      {files.photos.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.photos.map((p) => (
            <PhotoTile key={p.id} item={p} onOpen={onOpenPhoto} compact className="size-16" />
          ))}
        </div>
      )}
      {files.documents.length > 0 && (
        <ul className="divide-y divide-line">
          {files.documents.map((d) => (
            <DocumentRow key={d.id} item={d} />
          ))}
        </ul>
      )}
      {missing && (
        <p className="inline-flex items-center gap-1 text-[11px] text-fg-muted">
          <Paperclip aria-hidden className="size-3" />
          {missing}
        </p>
      )}
    </div>
  );
}

function FeedEntry({ row, now }: { row: FeedRow; now: Date }) {
  const time = feedTime(row.at, now);
  const body = (
    <>
      <ActivityGlyph type={row.type} color={row.tint} size={34} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <span className="truncate text-sm font-semibold text-fg">{row.title}</span>
          <time dateTime={row.at.toISOString()} title={row.at.toLocaleString()} className="shrink-0 text-[11px] text-fg-muted">
            {time}
          </time>
        </div>
        {row.recordId && (
          <span className={cn('truncate text-xs font-medium', row.recordName ? 'text-accent' : 'text-fg-subtle')}>
            {row.recordName ?? 'Store not available'}
          </span>
        )}
        {row.transition && <span className="text-xs text-fg-muted">{row.transition}</span>}
        {row.notes && <p className="line-clamp-3 text-xs break-words text-fg-muted">{row.notes}</p>}
        {row.duration && (
          <span className="inline-flex items-center gap-1 text-[11px] text-fg-muted">
            <Clock aria-hidden className="size-3" />
            {row.duration}
          </span>
        )}
        <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-fg-muted">
          {row.user && <Avatar name={fullName(row.user)} colorHex={row.user.colorHex} size={16} label={null} />}
          <span className="truncate">{row.byLine}</span>
        </span>
      </div>
    </>
  );
  const classes = 'flex min-w-0 items-start gap-3 px-4 py-3';
  if (!row.href) return <div className={classes}>{body}</div>;
  // No aria-label: the link's name comes from its text, so the note preview and details stay readable.
  return (
    <Link to={row.href} className={cn(classes, 'transition-colors hover:bg-surface-2')}>
      {body}
    </Link>
  );
}
